import React, { useState, useEffect } from "react";
import { Form, Row, Col, Card, Badge } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import Button from "../../../components/shared/buttons/button";
import { useUpdateRolePermissionsMutation } from "./rolePermissionApi";
import { toast } from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";
import "./rolePermission.css";

const RolePermissionModal = ({ show, setShow, role, permissionsList, onUpdate }) => {
  const { _t } = useTranslator();
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [updateRolePermissions, { isLoading: isUpdating }] = useUpdateRolePermissionsMutation();

  // Group permissions by module
  const groupedPermissions = permissionsList.reduce((acc, permission) => {
    const module = permission.module;
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {});

  useEffect(() => {
    if (role && role.permissions) {
      const rolePermissionIds = role.permissions.map(p => p.id);
      setSelectedPermissions(rolePermissionIds);
    } else {
      setSelectedPermissions([]);
    }
  }, [role, show]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleModuleToggle = (modulePermissions) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modulePermissionIds.includes(id)));
    } else {
      setSelectedPermissions(prev => {
        const newPermissions = [...prev];
        modulePermissionIds.forEach(id => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
        return newPermissions;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateRolePermissions({
        roleId: role.id,
        permissions: selectedPermissions
      }).unwrap();
      
      setShow(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Update permissions error:", error);
      toast.error(
        error?.data?.message || 
        _t("error_updating_permissions", "Unable to update the permissions. Please try again.")
      );
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const getModuleStats = (modulePermissions) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const selectedCount = modulePermissionIds.filter(id => selectedPermissions.includes(id)).length;
    const totalCount = modulePermissionIds.length;
    return { selectedCount, totalCount };
  };

  if (!role) return null;

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      title={`${_t("edit_permissions_for", "Edit Permissions for")} "${role.name}"`}
      className="role-permission-modal"
    >
      <Form onSubmit={handleSubmit}>
        <div className="role-info-section mb-3">
          <Row>
            <Col md={4}>
              <strong>{_t("role_name", "Role Name")}:</strong> {role.name}
            </Col>
            <Col md={4}>
              <strong>{_t("role_type", "Role Type")}:</strong> {role.role_type}
            </Col>
            <Col md={4}>
              <strong>{_t("working_area", "Working Area")}:</strong> {role?.working_area || _t("all_areas", "All Areas")}
            </Col>
          </Row>
        </div>

        <div className="permissions-section" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <h6 className="mb-3">
            {_t("permissions", "Permissions")} 
            <Badge bg="secondary" className="ms-2">
              {selectedPermissions.length} / {permissionsList.length} {_t("selected", "selected")}
            </Badge>
          </h6>

          {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
            const { selectedCount, totalCount } = getModuleStats(modulePermissions);
            const allSelected = selectedCount === totalCount;
            const someSelected = selectedCount > 0 && selectedCount < totalCount;

            return (
              <Card key={module} className="mb-3 permission-card">
                <Card.Header 
                  className="d-flex justify-content-between align-items-center"
                  onClick={() => handleModuleToggle(modulePermissions)}
                  style={{ 
                    backgroundColor: allSelected ? '#fff0ea' : someSelected ? '#f8f9fa' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={allSelected}
                      ref={input => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={() => handleModuleToggle(modulePermissions)}
                      className="me-2"
                    />
                    <strong className="text-capitalize">
                      {_t(module, module)} {_t("module", "Module")}
                    </strong>
                  </div>
                  <Badge 
                    bg={allSelected ? "primary" : someSelected ? "warning" : "secondary"}
                    style={{ backgroundColor: allSelected ? "#ee7225" : undefined }}
                  >
                    {selectedCount} / {totalCount}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {modulePermissions.map((permission) => (
                      <Col md={6} key={permission.id} className="mb-2">
                        <Form.Check
                          type="checkbox"
                          id={`permission-${permission.id}`}
                          label={_t(permission.name, permission.name.replace(/_/g, ' '))}
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>

        <div className="modal-footer-buttons mt-3 d-flex justify-content-end gap-2">
          <Button 
            label={_t("cancel", "Cancel")}
            onClick={handleClose}
            className="borderRedBtn"
          />
          <Button 
            label={isUpdating ? _t("updating", "Updating...") : _t("update_permissions", "Update Permissions")}
            type="submit"
            disabled={isUpdating}
          />
        </div>
      </Form>
    </CommonModal>
  );
};

export default RolePermissionModal;
